import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "./i18n";

const REVIEWS = [
  { name: "Alexander Brunner", quote: "Super friendly and inspiring atmosphere." },
  { name: "Elisabetta Franceschin", quote: "Very welcoming and friendly." },
  { name: "Antoine Fabri", quote: "Very respectful and welcoming." },
  { name: "Talita Lid", quote: "Tolle BJJ-Schule mit super Atmosphäre!" },
  { name: "Dario Wernet", quote: "Team und Stimmung sind sehr familiär!" },
  { name: "Mert Maraslioglu", quote: "Top Stimmung & top Trainer!" },
];

function StarRating({ label }) {
  return (
    <div className="home-reviews__rating" aria-label={label}>
      <span className="home-reviews__stars" aria-hidden="true">★★★★★</span>
      <strong>5.0</strong>
      <span>Google</span>
    </div>
  );
}

export default function HomepageReviewsInjector() {
  const { language, content: { site } } = useI18n();
  const [mountNode, setMountNode] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const anchor = document.querySelector("#programs");
    if (!anchor?.parentNode) return undefined;

    let node = document.getElementById("homepage-reviews-root");
    const created = !node;
    if (!node) {
      node = document.createElement("div");
      node.id = "homepage-reviews-root";
      anchor.parentNode.insertBefore(node, anchor);
    }
    setMountNode(node);

    return () => {
      setMountNode(null);
      if (created && node?.parentNode) node.parentNode.removeChild(node);
    };
  }, []);

  useEffect(() => {
    if (paused || typeof window === "undefined") return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % REVIEWS.length), 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const visibleReviews = useMemo(
    () => [0, 1, 2].map((offset) => REVIEWS[(index + offset) % REVIEWS.length]),
    [index],
  );

  if (!mountNode) return null;

  const copy = language === "de"
    ? {
        eyebrow: "WAS UNSERE MITGLIEDER SAGEN",
        title: "5 Sterne. Vor allem aber: eine Community, in der man gerne trainiert.",
        intro: "Kurze Stimmen aus öffentlichen Bewertungen unseres Zürcher Dojos.",
        rating: "5 von 5 Sternen auf Google",
        link: "Bewertungen auf Google ansehen",
        review: "Kundenstimme",
        nav: "Kundenstimmen auswählen",
      }
    : {
        eyebrow: "WHAT OUR MEMBERS SAY",
        title: "5 stars. More importantly: a community people enjoy training with.",
        intro: "Short excerpts from public reviews of our Zurich dojo.",
        rating: "5 out of 5 stars on Google",
        link: "Read reviews on Google",
        review: "Customer review",
        nav: "Choose customer reviews",
      };

  const googleUrl = site.dojos.find((dojo) => dojo.id === "zurich")?.map_url || site.instagram_profile;

  return createPortal(
    <section
      className="home-reviews section-pad"
      aria-labelledby="home-reviews-title"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="section-shell">
        <div className="home-reviews__head">
          <div>
            <p className="eyebrow">{copy.eyebrow}</p>
            <h2 id="home-reviews-title">{copy.title}</h2>
          </div>
          <div className="home-reviews__summary">
            <StarRating label={copy.rating} />
            <p>{copy.intro}</p>
            <a className="text-link" href={googleUrl} target="_blank" rel="noreferrer">{copy.link} →</a>
          </div>
        </div>

        <div className="home-reviews__grid" aria-live="polite">
          {visibleReviews.map((review, cardIndex) => (
            <article className="home-review-card" key={`${review.name}-${index}-${cardIndex}`}>
              <div className="home-review-card__stars" aria-hidden="true">★★★★★</div>
              <blockquote>“{review.quote}”</blockquote>
              <footer><strong>{review.name}</strong><span>{copy.review}</span></footer>
            </article>
          ))}
        </div>

        <div className="home-reviews__dots" aria-label={copy.nav}>
          {REVIEWS.map((review, dotIndex) => (
            <button
              key={review.name}
              type="button"
              className={dotIndex === index ? "active" : ""}
              aria-label={`${dotIndex + 1}: ${review.name}`}
              aria-pressed={dotIndex === index}
              onClick={() => setIndex(dotIndex)}
            />
          ))}
        </div>
      </div>
    </section>,
    mountNode,
  );
}
